import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { User } from '@/features/course-progress/api/types';
import { AssessmentGrid } from '@/features/map-profile/components/AssessmentGrid';
import { ErrorState } from '@/features/map-profile/components/ErrorState';
import { LoadingState } from '@/features/map-profile/components/LoadingState';
import { PdfViewer } from '@/features/map-profile/components/PdfViewer';
import { StudentSelector } from '@/features/map-profile/components/StudentSelector';
import { useMapProfile } from '@/features/map-profile/queries/useMapProfile';
import { AppPageContent } from '@/features/shared/components/AppPageContent';
import { AppPageHeader } from '@/features/shared/components/AppPageHeader';
import { useOneRosterAuth } from '@/hooks/useOneRosterAuth';
import { UserRoles } from '@/lib/constants/auth.constants.ts';
import { requireRole } from '@/lib/validation';

export const Route = createFileRoute('/_app/app/map-profile')({
  component: MapProfile,
  ssr: false,
  beforeLoad: requireRole([
    UserRoles.ADMINISTRATOR,
    UserRoles.PARENT,
    UserRoles.AIDE,
  ]),
});

function MapProfile() {
  const { user, children } = useOneRosterAuth();
  const [selectedStudent, setSelectedStudent] = useState<
    User | undefined | null
  >(children[0] || user);
  const [pdfViewerState, setPdfViewerState] = useState<{
    isOpen: boolean;
    pdfUrl: string;
    title: string;
  }>({ isOpen: false, pdfUrl: '', title: '' });

  const {
    data: mapData,
    isLoading,
    error,
    refetch,
  } = useMapProfile(selectedStudent);

  const handleViewPdf = (pdfUrl: string, title: string) => {
    setPdfViewerState({ isOpen: true, pdfUrl, title });
  };

  const handleClosePdfViewer = () => {
    setPdfViewerState({ isOpen: false, pdfUrl: '', title: '' });
  };

  const handleRefreshPdfData = async () => {
    const result = await refetch();
    if (result.data && pdfViewerState.isOpen) {
      // Find the updated PDF URL for the current report
      const reports = result.data.data || [];
      const currentReport = reports.find(
        (report) => report.title === pdfViewerState.title
      );
      if (currentReport?.pdfUrl) {
        setPdfViewerState((prev) => ({
          ...prev,
          pdfUrl: currentReport.pdfUrl,
        }));
      }
    }
  };

  return (
    <AppPageContent>
      <AppPageHeader
        title="Map Profile"
        breadcrumbItems={[
          {
            label: 'App',
            link: { to: '/app' },
          },
          {
            label: 'Map Profile',
            link: { to: '/app/map-profile' },
          },
        ]}
      />
      <StudentSelector
        selectedStudent={selectedStudent}
        onStudentSelect={setSelectedStudent}
      />
      <div className="px-4 pb-10 w-full max-w-full">
        {isLoading && selectedStudent ? (
          <LoadingState selectedStudent={selectedStudent} />
        ) : error && selectedStudent ? (
          <ErrorState error={error} selectedStudent={selectedStudent} />
        ) : (
          <AssessmentGrid reports={mapData?.data} onViewPdf={handleViewPdf} />
        )}
      </div>

      <PdfViewer
        isOpen={pdfViewerState.isOpen}
        onClose={handleClosePdfViewer}
        pdfUrl={pdfViewerState.pdfUrl}
        title={pdfViewerState.title}
        onRefresh={handleRefreshPdfData}
      />
    </AppPageContent>
  );
}
